import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ModeContextProps } from './Contexts/ModeContext';
import ModeContext from './Contexts/ModeContext';
import Mode from './Enums/Mode';
import { ThemeProvider } from '@mui/material/styles';
import type { SettingsContextProps } from './Contexts/SettingsContext';
import SettingsContext from './Contexts/SettingsContext';
import darkModeTheme from './Themes/DarkModeTheme';
import lightModeTheme from './Themes/LightModeTheme';
import AppTheme from './Enums/AppTheme';
import PageWrapperComponent from './Components/PageWrapper/PageWrapperComponent';
import type { JSX } from 'react';
import ApiService from './Services/Api/ApiService';
import LocalStorageService from './Services/LocalStorage/LocalStorageService';
import type { LocalStorageContextProps } from './Contexts/LocalStorageContext';
import LocalStorageContext from './Contexts/LocalStorageContext';
import type CurrentUser from './Models/CurrentUserModel';
import type SystemInfo from './Models/SystemInfoModel';
import { appVersion, debugApiBaseUrl, minApiVersion, productionApiBaseUrl } from './Constants';
import ErrorPage from './Pages/ErrorPage';
import type { ApiContextProps } from './Contexts/ApiContext';
import ApiContext from './Contexts/ApiContext';
import type { UserContextProps } from './Contexts/UserContext';
import UserContext from './Contexts/UserContext';
import MainPageRouter from './Routers/MainPageRouter';
import { checkVersion } from 'shouldve-been-js';
import LoginPage from './Pages/LoginPage';
import type { SystemContextProps } from './Contexts/SystemContext';
import SystemContext from './Contexts/SystemContext';
import type { TokenContextProps } from './Contexts/TokenContext';
import TokenContext from './Contexts/TokenContext';
import NoConnectionPage from './Pages/NoConnectionPage';
import type { BugReportContextProps } from './Contexts/BugReportContext';
import BugReportContext from './Contexts/BugReportContext';
import type BugReport from './Models/BugReportModel';
import { useTitle } from 'shouldve-been-react';
import type { RunContextProps } from './Contexts/RunContext';
import RunContext from './Contexts/RunContext';

function App(): JSX.Element {

    const [mode, setMode] = useState<Mode>(Mode.LOGIN);
    const [theme, setTheme] = useState<AppTheme>(AppTheme.LIGHT_MODE);
    const [api, setApi] = useState<ApiService | null>(null);
    const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
    const [lastActivity, setLastActivity] = useState<Date | null>(null);
    const [health, setHealth] = useState<boolean>(false);

    const [apiVersion, setApiVersion] = useState<string | null>(null);
    const [minAppVersion, setMinAppVersion] = useState<string | null>(null);
    const [systemName, setSystemName] = useState<string | null>(null);
    
    const [token, setToken] = useState<string | null>(null);
    const [expiration, setExpiration] = useState<Date | null>(null);

    const [bugReports, setBugReports] = useState<BugReport[]>([]);

    const [title, setTitle] = useState<string>('K-Fleet Vessel Manager');
    
    //used to prevent error messages from showing before the application is initialized
    const [initializedFromLocalStorage, setInitializedFromLocalStorage] = useState<boolean>(false);
    const [connectedDataFetched, setConnectedDataFetched] = useState<boolean>(false);

    const [errorMessageClient, setErrorMessageClient] = useState<string | null>(null);
    const [errorMessageSystem, setErrorMessageSystem] = useState<string | null>(null);

    useTitle(title);

    const isDebugMode: boolean = window.location.href.includes('localhost');

    const localStorageService: LocalStorageService = useMemo<LocalStorageService>(() => new LocalStorageService('vesselManager'), []);

    const addBugReport: (newBugReport: BugReport) => void = useCallback((newBugReport: BugReport) => {
        
        setBugReports((prev: BugReport[]) => [...prev, newBugReport]);
    }, []);

    const modeContextValues: ModeContextProps = useMemo<ModeContextProps>(() => {
        return {
            mode: mode,
            setMode: setMode,
        };
    }, [mode]);

    const userContextValues: UserContextProps = useMemo<UserContextProps>(() => {
        return {
            currentUser: currentUser,
            setCurrentUser: setCurrentUser,
            lastActivity: lastActivity,
            setLastActivity: setLastActivity,
        };
    }, [currentUser, lastActivity]);

    const tokenContextValues: TokenContextProps = useMemo<TokenContextProps>(() => {
        return {
            token: token,
            setToken: setToken,
            expiration: expiration,
            setExpiration: setExpiration,
        };
    }, [expiration, token]);

    const apiContextValues: ApiContextProps = useMemo<ApiContextProps>(() => {
        return {
            api: api,
            setApi: setApi,
        };
    }, [api]);

    const localStorageContextValues: LocalStorageContextProps = useMemo<LocalStorageContextProps>(() => {
        return {
            localStorageService: localStorageService,
        };
    }, [localStorageService]);

    const settingsContextValues: SettingsContextProps = useMemo<SettingsContextProps>(() => {
        return {
            theme: theme,
            setTheme: setTheme,
        };
    }, [theme]);

    const systemContextValues: SystemContextProps = useMemo<SystemContextProps>(() => {
        return {
            systemName: systemName,
            setSystemName: setSystemName,
            apiVersion: apiVersion,
            setApiVersion: setApiVersion,
            minAppVersion: minAppVersion,
            setMinAppVersion: setMinAppVersion,
        };
    }, [apiVersion, minAppVersion, systemName]);

    const bugReportContextValues: BugReportContextProps = useMemo<BugReportContextProps>(() => {
        return {
            bugReports: bugReports,
            setBugReports: setBugReports,
            addBugReport: addBugReport,
        };
    }, [addBugReport, bugReports]);

    const runContextValues: RunContextProps = useMemo<RunContextProps>(() => {
        return {
            setTitle: setTitle,
        };
    }, []);
    
    const renderBody: JSX.Element = useMemo<JSX.Element>(() => {

        if (errorMessageClient !== null) {
            return (
                <ErrorPage 
                    errorMessage={errorMessageClient}
                />
            );

        } else if (!health) {
            return <NoConnectionPage />;
            
        } else if (errorMessageSystem !== null) {
            return (
                <ErrorPage 
                    errorMessage={errorMessageSystem}
                />
            );

        } else if (mode === Mode.LOGIN) {
            return <LoginPage />; 

        } else if (mode === Mode.MAIN) {
            return <MainPageRouter />;

        } else { //should not happen
            return <ErrorPage
                errorMessage='An unexpected error occurred'
            />;
        }
    }, [errorMessageClient, errorMessageSystem, health, mode]);

    useEffect(() => {

        const newApi: ApiService = isDebugMode
            ? new ApiService(debugApiBaseUrl, addBugReport)
            : new ApiService(productionApiBaseUrl, addBugReport);

        setApi(newApi);
    }, [addBugReport, isDebugMode]);

    useEffect(() => { //check health

        let mounted: boolean = true;
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time, in case e.g. the api call is heavily delayed

        async function checkHealth(): Promise<void> {

            if (isRunning) {
                return;
            }

            isRunning = true;
        
            try {
                const newHealth: boolean = await api?.getHealth() ?? false;

                if (!mounted) {
                    return;
                }

                if (newHealth) {
                    setHealth(true);
                    return;
                }

                //if fails, wait 2 seconds and try once more before doing anything
                await new Promise(r => setTimeout(r, 2000));
                const retryHealth: boolean = await api?.getHealth() ?? false;

                if (!mounted) {
                    return;
                }

                setHealth(retryHealth);
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        void checkHealth(); //run once immediately
        const interval: ReturnType<typeof setInterval> = setInterval(checkHealth, 10000); //run again every 10th second
         
        return () => {
            clearInterval(interval);
            mounted = false;
        };
    }, [api]);

    useEffect(() => { //initialize values from local storage
     
        const newMode: Mode | null = localStorageService.getMode();
        const newTheme: AppTheme | null = localStorageService.getTheme();
        const newCurrentUser: CurrentUser | null = localStorageService.getCurrentUser();
        const newLastActivity: Date | null = localStorageService.getLastActivity();
        const newToken: string | null = localStorageService.getToken();
        const newExpiration: Date | null = localStorageService.getExpiration();
        
        if (newMode !== null) {
            setMode(newMode);
        }

        if (newTheme !== null) {
            setTheme(newTheme);
        }

        if (newCurrentUser !== null) {
            setCurrentUser(newCurrentUser);
        }

        if (newLastActivity !== null) {
            setLastActivity(newLastActivity);
        }

        if (newToken !== null) {
            setToken(newToken);
        }

        if (newExpiration !== null) {
            setExpiration(newExpiration);
        }

        setInitializedFromLocalStorage(true);

    }, [localStorageService]);

    useEffect(() => {

        let mounted: boolean = true;
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time, in case e.g. the api call is heavily delayed

        async function fetchVitalData(): Promise<void> {
            
            if (isRunning) {
                return;
            }

            isRunning = true;

            try {
                const [systemInfo, customerName]: [SystemInfo | null, string | null] = await Promise.all([
                    api?.getSystemInfo() ?? null,
                    api?.getCustomerName(token) ?? null,
                ]);
                
                if (mounted) {

                    if (systemInfo !== null) {
                        setApiVersion(systemInfo.apiVersion);
                        setMinAppVersion(systemInfo.minAppVersion);
                    }

                    if (customerName !== null) {
                        setSystemName(customerName);
                    }

                    setConnectedDataFetched(true);
                }
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        void fetchVitalData(); //run once immediately
        const interval: ReturnType<typeof setInterval> = setInterval(fetchVitalData, 300000); //run again every 5th minute
        
        return () => {
            clearInterval(interval);
            mounted = false;
        };
    }, [api, token]);

    useEffect(() => { //check if anything client related is wrong

        if (!LocalStorageService.isLocalStorageAvailable()) {
            setErrorMessageClient('Your browser does not support localstorage. This is required. Please try another browser.');
        } else {
            setErrorMessageClient(null);
        }
    }, []);

    useEffect(() => { //check if anything system related is wrong

        if (!initializedFromLocalStorage || !connectedDataFetched) { //not ready to check yet
            return;
        }

        if (minAppVersion === null || appVersion === null || !checkVersion(appVersion, minAppVersion)) {
            setErrorMessageSystem('You are on app version ' + appVersion + ', while the system requires at least version ' + (minAppVersion ?? 'ERROR') + '. Please try to refresh the page to update, and if that doesn\'t help, contact customer support.');
        } else if (minApiVersion === null || apiVersion === null || !checkVersion(apiVersion, minApiVersion)) {
            setErrorMessageSystem('Your app version requires a newer system version. The current system is on version ' + (apiVersion ?? 'ERROR') + ', while your app version requires at least version ' + minApiVersion + '. Please contact customer support.');
        } else {
            setErrorMessageSystem(null);
        }
    }, [apiVersion, connectedDataFetched, initializedFromLocalStorage, minAppVersion]);

    useEffect(() => { //check for token expiration

        function checkTokenExpiration(): void {

            if (expiration === null || currentUser === null || !health) {
                return;
            }
    
            if (expiration.getTime() < Date.now() + 2 * 60 * 1000) { //if token expires in less than two minutes
                
                //duplicate of handleLogout as the contexts are not available here
                
                setCurrentUser(null);
                localStorageService.clearCurrentUser();

                setLastActivity(null);
                localStorageService.clearLastActivity();

                setMode(Mode.LOGIN);
                localStorageService.setMode(Mode.LOGIN);

                setApiVersion(null);
                
                setMinAppVersion(null);

                setToken(null);
                localStorageService.setToken(null);

                setExpiration(null);
                localStorageService.setExpiration(null);
            }
        }

        checkTokenExpiration();
        const interval: ReturnType<typeof setInterval> = setInterval(checkTokenExpiration, 60000); //run again every minute

        return () => {
            clearInterval(interval);
        };
    }, [api, currentUser, expiration, health, localStorageService, token]);

    useEffect(() => { //check if timed out

        function checkTimeout(): void {

            const timeoutHours: number = 1; //times out after 1 hour

            if (lastActivity === null || lastActivity.getTime() < (new Date()).getTime() - (1000 * 60 * 60 * timeoutHours)) {

                //duplicate of handleLogout as the contexts are not available here
                
                setCurrentUser(null);
                localStorageService.clearCurrentUser();

                setLastActivity(null);
                localStorageService.clearLastActivity();

                setMode(Mode.LOGIN);
                localStorageService.setMode(Mode.LOGIN);

                setApiVersion(null);
                
                setMinAppVersion(null);

                setToken(null);
                localStorageService.setToken(null);

                setExpiration(null);
                localStorageService.setExpiration(null);
            }
        }

        if (initializedFromLocalStorage && mode === Mode.MAIN) {
            checkTimeout(); //run once immediately
            const interval: ReturnType<typeof setInterval> = setInterval(checkTimeout, 60000); //run again every minute
            
            return () => clearInterval(interval);
        } else {
            return undefined;
        }
    }, [initializedFromLocalStorage, lastActivity, localStorageService, mode]);

    return (
        <ModeContext.Provider value={modeContextValues}>
            <UserContext.Provider value={userContextValues}>
                <TokenContext.Provider value={tokenContextValues}>
                    <ApiContext.Provider value={apiContextValues}>
                        <LocalStorageContext.Provider value={localStorageContextValues}>
                            <SettingsContext.Provider value={settingsContextValues}>
                                <SystemContext.Provider value={systemContextValues}>
                                    <BugReportContext.Provider value={bugReportContextValues}>
                                        <RunContext.Provider value={runContextValues}>
                                            <ThemeProvider theme={theme === AppTheme.DARK_MODE ? darkModeTheme : lightModeTheme}>
                                                <PageWrapperComponent
                                                    renderBody={renderBody}
                                                />
                                            </ThemeProvider>
                                        </RunContext.Provider>
                                    </BugReportContext.Provider>
                                </SystemContext.Provider>
                            </SettingsContext.Provider>
                        </LocalStorageContext.Provider>
                    </ApiContext.Provider>
                </TokenContext.Provider>
            </UserContext.Provider>
        </ModeContext.Provider>
    );
}

export default App;